import type { CallRoutingRule } from "@/features/connectors/types";
import { TEMPORARY_CALLER_NUMBERS_MAX } from "@/features/connectors/types";
import { useTranslation } from "@/features/utils/i18n";
import { toast } from "sonner";
import { cleanDestinations } from "../destination-list";
import { RING_BEFORE_AGENT_MAX_S, type ConnectorFormState } from "./state";
import type { ConnectorFormData, MailServerForm } from "./types";

const trimServer = (server: MailServerForm): MailServerForm => ({
  ...server,
  host: server.host.trim(),
  port: server.port.trim(),
  username: server.username.trim(),
});

/** The `t` of `useTranslation("connectors")`, passed in by the form. */
type Translate = ReturnType<typeof useTranslation>["t"];

/**
 * Check the form and build the payload, or explain the first problem to the
 * user and give up.
 *
 * Returns `null` once it has toasted, so the caller only has to decide whether
 * to submit. Every rule here also exists server-side — this is what keeps the
 * user from discovering it as a 400.
 */
export function validateConnectorForm(
  state: ConnectorFormState,
  mode: "create" | "edit",
  t: Translate,
): ConnectorFormData | null {
  const {
    type,
    status,
    capabilities,
    agentId,
    accountId,
    authToken,
    twilioRegion,
    phoneNumbers,
    whatsappNumbers,
    routingRules,
    geoFilter,
    outboundFilter,
    fallbackDestinations,
    agentOffEnabled,
    agentOffScheduleId,
    ringBeforeAgentSeconds,
    allowedOrigins,
    callerNumbers,
    demoNumber,
    codeRouting,
    defaultConnector,
    webCallEnabled,
    publicCallbackEnabled,
    sipRegistration,
    emailAccount,
  } = state;

  // The mailbox. Presence only: the server owns the format rules and the
  // "public internet only" check on the hosts, and its message names the field.
  if (type === "email") {
    const missing =
      !emailAccount.address.trim() ||
      !emailAccount.smtp.host.trim() ||
      !emailAccount.imap.host.trim();
    if (missing) {
      toast.error(
        t(
          "connectors.email.errors.required",
          "Renseignez l'adresse e-mail et les serveurs SMTP et IMAP.",
        ),
      );
      return null;
    }
    // Both passwords are needed to create; the same one often serves both, so
    // an edit may leave either empty to keep what is stored.
    if (
      mode === "create" &&
      (!emailAccount.smtp.password || !emailAccount.imap.password)
    ) {
      toast.error(
        t(
          "connectors.email.errors.passwordRequired",
          "Renseignez le mot de passe de la boîte, pour l'envoi (SMTP) et pour la réception (IMAP).",
        ),
      );
      return null;
    }
  }

  // The extension HeyUp registers as. Only presence is checked here: the
  // server owns the format rules (a SIP URI is built from these) and its
  // message names the field.
  if (type === "sip") {
    if (!sipRegistration.server.trim() || !sipRegistration.username.trim()) {
      toast.error(
        t(
          "connectors.sip.errors.required",
          "Renseignez l'adresse du standard et le numéro de poste.",
        ),
      );
      return null;
    }
    if (mode === "create" && !sipRegistration.password) {
      toast.error(
        t(
          "connectors.sip.errors.passwordRequired",
          "Renseignez le mot de passe du poste.",
        ),
      );
      return null;
    }
  }

  if (type === "twilio" && !accountId) {
    toast.error(t("connectors.messages.accountIdRequired"));
    return null;
  }

  if (mode === "create" && type === "twilio" && !authToken) {
    toast.error(t("connectors.messages.authTokenRequired"));
    return null;
  }

  if (type === "heyup" && !phoneNumbers.trim()) {
    toast.error(
      t(
        "connectors.heyup.errors.numberRequired",
        "Choisissez le numéro HeyUp sur lequel votre agent doit répondre.",
      ),
    );
    return null;
  }

  if (capabilities.length === 0) {
    toast.error(t("connectors.messages.capabilityRequired"));
    return null;
  }

  const callerNumbersArray = callerNumbers
    .split(",")
    .map((num) => num.trim())
    .filter(Boolean);

  if (type === "temporary") {
    if (callerNumbersArray.length === 0) {
      toast.error(
        t(
          "connectors.temporary.errors.callerNumbersRequired",
          "Ajoutez au moins un numéro depuis lequel vous appellerez.",
        ),
      );
      return null;
    }
    if (callerNumbersArray.length > TEMPORARY_CALLER_NUMBERS_MAX) {
      toast.error(
        t("connectors.temporary.errors.callerNumbersMax", {
          max: TEMPORARY_CALLER_NUMBERS_MAX,
          defaultValue: `Vous pouvez déclarer au maximum ${TEMPORARY_CALLER_NUMBERS_MAX} numéros.`,
        }),
      );
      return null;
    }
  }

  // Validate & normalize caller-routing rules
  const sanitizedRules: CallRoutingRule[] = [];
  for (const rule of routingRules) {
    const pattern = rule.pattern.trim();
    if (!pattern) {
      toast.error(t("connectors.routing.errors.patternRequired"));
      return null;
    }
    // The backend never matches a bare "*" (routing.ts `ruleMatches`): saved,
    // it is a rule that silently does nothing.
    if (pattern.replace(/[\s\-().]/g, "") === "*") {
      toast.error(t("connectors.routing.errors.catchAllPattern"));
      return null;
    }
    if (rule.action === "agent" && !rule.agent_id) {
      toast.error(t("connectors.routing.errors.agentRequired"));
      return null;
    }
    const transferDestinations = cleanDestinations(
      rule.transfer_destinations || [],
    );
    if (rule.action === "transfer" && transferDestinations.length === 0) {
      toast.error(t("connectors.routing.errors.transferTargetRequired"));
      return null;
    }

    const sanitized: CallRoutingRule = {
      id: rule.id,
      pattern,
      action: rule.action,
    };
    if (rule.action === "agent") {
      sanitized.agent_id = rule.agent_id;
    } else if (rule.action === "transfer") {
      // The chain is the only shape written from here on; the legacy pair is
      // left off the payload so the backend stops reading it for this rule.
      sanitized.transfer_destinations = transferDestinations;
    }
    sanitizedRules.push(sanitized);
  }

  const cleanedFallback = cleanDestinations(fallbackDestinations);

  if (agentOffEnabled && !agentOffScheduleId) {
    toast.error(t("connectors.routing.errors.agentOffScheduleRequired"));
    return null;
  }
  // With nowhere to send them, the backend lets the agent answer anyway: the
  // switch would look on and do nothing.
  if (agentOffEnabled && cleanedFallback.length === 0) {
    toast.error(t("connectors.routing.errors.agentOffFallbackRequired"));
    return null;
  }

  if (geoFilter.enabled && geoFilter.countries.length === 0) {
    toast.error(
      t(
        "connectors.geo.errors.countriesRequired",
        "Sélectionnez au moins un pays pour le filtrage géographique.",
      ),
    );
    return null;
  }

  return {
    type,
    status,
    capabilities,
    agentId,
    accountId,
    authToken,
    twilioRegion: twilioRegion.trim().toLowerCase(),
    phoneNumbers,
    whatsappNumbers,
    routingRules: sanitizedRules,
    geoFilter,
    outboundFilter,
    fallbackDestinations: cleanedFallback,
    agentOffScheduleId: agentOffEnabled ? agentOffScheduleId : "",
    ringBeforeAgentSeconds: Math.min(
      RING_BEFORE_AGENT_MAX_S,
      Math.max(0, Math.round(ringBeforeAgentSeconds || 0)),
    ),
    allowedOrigins: allowedOrigins.trim(),
    callerNumbers: callerNumbersArray.join(", "),
    demoNumber,
    // Only a demo number routes by code.
    codeRouting: demoNumber && codeRouting,
    defaultConnector,
    webCallEnabled,
    publicCallbackEnabled,
    sipRegistration: {
      ...sipRegistration,
      server: sipRegistration.server.trim(),
      port: sipRegistration.port.trim(),
      username: sipRegistration.username.trim(),
      authUsername: sipRegistration.authUsername.trim(),
      outboundPrefix: sipRegistration.outboundPrefix.trim(),
    },
    emailAccount: {
      address: emailAccount.address.trim(),
      displayName: emailAccount.displayName.trim(),
      mailbox: emailAccount.mailbox.trim(),
      saveToSent: emailAccount.saveToSent,
      sentFolder: emailAccount.sentFolder.trim(),
      smtp: trimServer(emailAccount.smtp),
      imap: trimServer(emailAccount.imap),
    },
  };
}
